import { useEffect, useRef, useState } from "react";
import { FiChevronDown } from "react-icons/fi";

import type { UserGuideHeading } from "../../entities/user-guide";

/**
 * Оглавление. На широком экране это колонка справа, которая едет за чтением и
 * подсвечивает текущий раздел; на узком — раскрывающаяся строка над статьёй, в
 * которой сразу видно, где читатель находится.
 */
export function GuideToc({
  headings,
  activeId,
  onSelect,
  variant,
}: {
  headings: readonly UserGuideHeading[];
  activeId: string | null;
  onSelect: (id: string) => void;
  variant: "rail" | "disclosure";
}) {
  // Пункты третьего уровня остаются, четвёртый — уже мелочь, в оглавлении он лишний.
  const items = headings.filter((heading) => heading.level <= 3);
  const current = items.find((item) => item.id === activeId) ?? items[0];
  const rail = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);

  // Активный пункт не должен уезжать за край колонки: подводим его к видимой
  // части самой колонки, не двигая страницу.
  useEffect(() => {
    const container = rail.current;
    const link = container?.querySelector<HTMLElement>(
      "[aria-current='location']",
    );
    if (container === null || container === undefined || !link) return;
    // Колонка — позиционированный предок ссылки, поэтому offsetTop считается от неё.
    const top = link.offsetTop;
    const bottom = top + link.offsetHeight;
    if (top < container.scrollTop) {
      container.scrollTop = top - 8;
    } else if (bottom > container.scrollTop + container.clientHeight) {
      container.scrollTop = bottom - container.clientHeight + 8;
    }
  }, [activeId]);

  if (items.length === 0) return null;

  const links = (
    <ol>
      {items.map((item) => (
        <li
          key={item.id}
          className={item.level === 3 ? "is-nested" : undefined}
        >
          <a
            href={`#${item.id}`}
            aria-current={item.id === current?.id ? "location" : undefined}
            onClick={(event) => {
              event.preventDefault();
              setOpen(false);
              onSelect(item.id);
            }}
          >
            {item.title}
          </a>
        </li>
      ))}
    </ol>
  );

  if (variant === "rail") {
    return (
      <nav ref={rail} className="guide-toc" aria-labelledby="guide-toc-title">
        <p id="guide-toc-title" className="guide-toc__title">
          Содержание
        </p>
        {links}
      </nav>
    );
  }

  return (
    <nav className="guide-toc-inline" aria-label="Содержание инструкции">
      <button
        className="guide-toc-inline__summary"
        type="button"
        aria-expanded={open}
        aria-controls="guide-toc-inline-list"
        onClick={() => setOpen((value) => !value)}
      >
        <span>
          <strong>Содержание</strong>
          <small>{current?.title}</small>
        </span>
        <FiChevronDown aria-hidden="true" />
      </button>
      <div id="guide-toc-inline-list" hidden={!open}>
        {links}
      </div>
    </nav>
  );
}
